import { DataTableId } from '../../common/table/utils';
import { useCallback, useMemo } from 'react';
import { useTags } from '../dependencies/annotations/tags/useTags';
import { TagRow } from './types';
import { SortOrder } from '../../common/types';
import { tagsSortOptions } from './utils';
import { LayoutTableProps, LayoutTopBarProps } from '../building-blocks/content/types';
import { DownloadTagsCsv } from './DownloadTagsCsv';
import { DataTableRow } from '../../common/table/TableTypes';
import { SubPageTypes } from '../types';
import { useTabFiltersFunctions } from '../selected-object/filters/useTabFilterFunctions';
import { BaseLayout } from '../building-blocks/content/BaseLayout';
import { useExpandedMode } from '../../pages/configuration-canvas/panels-reserve-space/useExpandedMode';
import { useDependencies } from '../dependencies/hooks/useDependencies';
import { useEffect } from 'react';

interface TagsCenterLayoutProps {
  columns: any[];
  rows: DataTableRow[];
  crmOrgId: string;
  label: string;
  pageType: SubPageTypes;
  sortProps: { sortBy: string; sortOrder: SortOrder };
}

export const TagsCenterLayout = ({
  columns,
  crmOrgId,
  rows,
  label,
  pageType,
  sortProps,
}: TagsCenterLayoutProps) => {
  const { handleCloseDependencies } = useDependencies(crmOrgId);
  const { isExpandedMode } = useExpandedMode();
  const { registerSortChange: _onSortChange } = useTabFiltersFunctions();
  const { onTagClick } = useTags(crmOrgId);
  const onSortChange = useCallback(
    (props: { sortBy: string; sortOrder: SortOrder }) => {
      const sortBy = props.sortBy === 'name' ? '_name' : props.sortBy;
      _onSortChange({ sortKey: sortBy, sortOrder: props.sortOrder });
    },
    [_onSortChange],
  );

  useEffect(() => {
    return () => {
      handleCloseDependencies();
    };
  }, [handleCloseDependencies]);

  const onRowClick = useCallback(
    (event: React.MouseEvent<HTMLTableCellElement, MouseEvent>, row: DataTableRow) => {
      const tagRow = row as TagRow;
      onTagClick(
        event,
        {
          value: tagRow.id,
          label: typeof tagRow._name === 'string' ? tagRow._name : tagRow._sfApiName,
          tagColor: tagRow.color,
        },
        true,
      );
    },
    [onTagClick],
  );

  const tableProps: LayoutTableProps = useMemo(
    () => ({
      tableId: DataTableId.DocumentationTagsCenterTable,
      onRowClick,
      onSortChange,
    }),
    [onRowClick, onSortChange],
  );

  const topBarProps: LayoutTopBarProps = useMemo(
    () => ({
      downloadCsvJsx: <DownloadTagsCsv items={rows as TagRow[]} label="Tags" columns={columns} />,
      label,
      sortOptions: tagsSortOptions,
      sortProps,
    }),
    [rows, columns, label, sortProps],
  );

  return (
    <BaseLayout
      dataTestId="tags-center-layout"
      crmOrgId={crmOrgId}
      isExpandedMode={isExpandedMode}
      columns={columns}
      rows={rows}
      topBarProps={topBarProps}
      tableProps={tableProps}
      pageType={pageType}
    />
  );
};
